import React, { FC, ReactElement, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  Rate,
  Row,
  Typography,
  message,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { ReviewResponse, ReviewError } from "../../../types/types";

import "./ProductReviews.css";
import { selectUserFromUserState } from "../../../redux-toolkit/user/user-selector";

interface ReviewData {
  author: string;
  message: string;
  rating: number;
}

type PropType = {
  reviews: ReviewResponse[];
  reviewErrors: Partial<ReviewError>;
  addReview: (data: ReviewData) => void | Promise<void>;
  form?: FormInstance<any>;
};

const ProductReviews: FC<PropType> = ({
  reviews,
  reviewErrors,
  addReview,
  form,
}): ReactElement => {
  const { messageError, ratingError } = reviewErrors;

  const currentUser = useSelector(selectUserFromUserState);
  const isLoggedIn = Boolean(currentUser && currentUser.email);
  const [posting, setPosting] = useState(false);

  const displayName = isLoggedIn
    ? currentUser?.firstName ||
      currentUser?.email?.split("@")[0] ||
      "User"
    : "";

  // ===== Compute rating summary =====
  const totalReviews = reviews.length;

  const avgRating =
    totalReviews > 0
      ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / totalReviews
      : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => Math.round(r.rating) === star).length,
  }));

  const onFinish = async (values: any) => {
    const payload: ReviewData = {
      author: displayName,
      rating: values.rating,
      message: values.message || "",
    };

    try {
      setPosting(true);
      const maybePromise = addReview(payload);
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        await maybePromise;
      }
      message.success("Review submitted successfully!");
      form?.resetFields(["rating", "message"]);
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || "Failed to submit review.");
    } finally {
      setPosting(false);
    }
  };

  const getInitial = (name?: string) =>
    name && name.length > 0 ? name.charAt(0).toUpperCase() : "U";

  return (
    <>
      {/* ===== RATING SUMMARY ===== */}
      {totalReviews > 0 && (
        <div className="fk-reviews-summary">
          <div className="fk-reviews-score">
            <div className="big-num">{avgRating.toFixed(1)}</div>
            <div className="big-star">★</div>
            <div className="big-count">
              {totalReviews} {totalReviews === 1 ? "Rating" : "Ratings"}
            </div>
          </div>

          <div className="fk-reviews-bars">
            {ratingCounts.map(({ star, count }) => (
              <div className="fk-bar-row" key={star}>
                <span className="fk-bar-label">{star} ★</span>
                <div className="fk-bar-bg">
                  <div
                    className="fk-bar-fill"
                    style={{
                      width: totalReviews > 0 ? `${(count / totalReviews) * 100}%` : "0%",
                    }}
                  />
                </div>
                <span className="fk-bar-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== ADD REVIEW FORM (logged in only) ===== */}
      {isLoggedIn && (
        <div className="fk-review-form-wrap">
          <div className="fk-review-form-title">Write a Review</div>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="Your rating"
              name="rating"
              help={ratingError}
              validateStatus={ratingError ? "error" : ""}
              rules={[{ required: true, message: "Please provide a rating" }]}
            >
              <Rate />
            </Form.Item>

            <Form.Item
              label="Message"
              name="message"
              help={messageError}
              validateStatus={messageError ? "error" : ""}
            >
              <Input.TextArea rows={3} placeholder="Share your experience..." />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={posting}
              className="fk-review-submit-btn"
            >
              Post Review
            </Button>
          </Form>
        </div>
      )}

      {/* ===== REVIEWS LIST ===== */}
      {totalReviews === 0 ? (
        <div className="fk-no-reviews">
          No reviews yet. Be the first to share your experience!
        </div>
      ) : (
        <Row>
          <Col span={24}>
            {reviews.map((review) => {
              const safeAuthor = review?.author || "Unknown User";
              const initial = getInitial(safeAuthor);
              const dateText = review?.date
                ? new Date(review.date).toLocaleDateString()
                : "";

              return (
                <Card key={review.id} className="fk-review-card">
                  <div className="fk-review-top-row">
                    <div className="fk-review-avatar">{initial}</div>
                    <div className="fk-review-name">{safeAuthor}</div>
                    <div className="fk-review-stars">
                      <Rate disabled value={review.rating} />
                    </div>
                    <div className="fk-review-date">{dateText}</div>
                  </div>
                  {review.message && (
                    <div className="fk-review-text">{review.message}</div>
                  )}
                </Card>
              );
            })}
          </Col>
        </Row>
      )}
    </>
  );
};

export default ProductReviews;